import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import { db } from '../../firebase';

const WORKSPACES = {
  lended: { label: 'Lended', active: 'bg-blue-600 text-white', dot: 'bg-blue-500' },
  saturn: { label: 'Saturn', active: 'bg-violet-600 text-white', dot: 'bg-violet-500' },
};

export default function DailyTodo() {
  const [workspace, setWorkspace] = useState('lended');
  const [tasks, setTasks] = useState([]);
  const [text, setText] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const today = new Date().toISOString().slice(0, 10);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'adminDailyTodos'));
      setTasks(snap.docs.map(item => ({ id: item.id, ...item.data() })).filter(item => !item.archived));
    } catch (error) {
      console.error('Unable to load daily tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, []);

  const visible = useMemo(() => tasks
    .filter(task => task.workspace === workspace)
    .sort((a, b) => Number(a.completed) - Number(b.completed) || String(b.date || '').localeCompare(String(a.date || ''))), [tasks, workspace]);
  const openCount = visible.filter(task => !task.completed).length;

  const addTask = async (event) => {
    event.preventDefault();
    const enteredTitle = text.trim() || notes.trim().split('\n')[0];
    if (!enteredTitle) {
      setError('Type a task before adding it.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const notesValue = text.trim() ? notes.trim() : '';
      const payload = { title: enteredTitle, notes: notesValue, workspace, date: today, completed: false, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
      const created = await addDoc(collection(db, 'adminDailyTodos'), payload);
      setTasks(current => [{ id: created.id, ...payload, createdAt: new Date() }, ...current]);
      setText(''); setNotes('');
    } catch (saveError) {
      console.error('Unable to add task:', saveError);
      setError('Could not save this task. Please refresh and try again.');
    } finally {
      setSaving(false);
    }
  };

  const patchTask = async (task, changes) => {
    setTasks(current => current.map(item => item.id === task.id ? { ...item, ...changes } : item));
    await updateDoc(doc(db, 'adminDailyTodos', task.id), { ...changes, updatedAt: serverTimestamp() });
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-sm font-semibold uppercase tracking-wider text-indigo-600">Daily command center</p><h2 className="mt-1 text-3xl font-bold tracking-tight text-gray-900">Today’s To-Do</h2><p className="mt-1 text-sm text-gray-500">{openCount} open {openCount === 1 ? 'task' : 'tasks'} in {WORKSPACES[workspace].label}</p></div>
        <div className="inline-flex self-start rounded-xl bg-gray-200 p-1">
          {Object.entries(WORKSPACES).map(([key, item]) => <button key={key} onClick={() => setWorkspace(key)} className={`rounded-lg px-5 py-2 text-sm font-semibold transition ${workspace === key ? item.active + ' shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>{item.label}</button>)}
        </div>
      </div>

      <form onSubmit={addTask} className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${WORKSPACES[workspace].dot}`} /><span className="text-sm font-semibold text-gray-900">New {WORKSPACES[workspace].label} task</span></div>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-gray-500">Task</label>
        <input value={text} onChange={e => { setText(e.target.value); if (error) setError(''); }} placeholder="What needs to get done?" className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-base font-medium text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white focus:ring-indigo-500" />
        <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-gray-500">Notes <span className="font-normal normal-case text-gray-400">(optional)</span></label>
        <textarea value={notes} onChange={e => { setNotes(e.target.value); if (error) setError(''); }} rows={2} placeholder="Add context, links, or next steps…" className="mt-1 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
        {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <div className="mt-3 flex justify-end"><button disabled={saving} className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:cursor-wait disabled:opacity-60"><PlusIcon className="h-4 w-4" /> {saving ? 'Saving…' : 'Add task'}</button></div>
      </form>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {loading ? <p className="p-8 text-center text-sm text-gray-500">Loading tasks…</p> : visible.length === 0 ? <div className="p-12 text-center"><CheckIcon className="mx-auto h-10 w-10 rounded-full bg-emerald-100 p-2 text-emerald-600" /><h3 className="mt-3 font-semibold text-gray-900">You’re clear</h3><p className="mt-1 text-sm text-gray-500">Add the first {WORKSPACES[workspace].label} task above.</p></div> : visible.map(task => (
          <div key={task.id} className={`group flex items-start gap-4 border-b border-gray-100 p-5 last:border-0 ${task.completed ? 'bg-gray-50/70' : 'hover:bg-gray-50/50'}`}>
            <button onClick={() => patchTask(task, { completed: !task.completed, completedAt: !task.completed ? serverTimestamp() : null })} className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-lg border-2 transition ${task.completed ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-300 bg-white hover:border-emerald-500'}`}>{task.completed && <CheckIcon className="h-4 w-4" />}</button>
            <div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><p className={`font-semibold ${task.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{task.title}</p>{task.date === today && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">Today</span>}</div>{task.notes && <p className={`mt-1 whitespace-pre-wrap text-sm leading-6 ${task.completed ? 'text-gray-400' : 'text-gray-600'}`}>{task.notes}</p>}</div>
            <button onClick={() => patchTask(task, { archived: true })} title="Remove task" className="rounded-lg p-2 text-gray-300 opacity-0 hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"><TrashIcon className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
